import { coerceNumberProperty } from '@angular/cdk/coercion';
import { ChangeDetectionStrategy, Component, input, InputSignal, OnChanges, Signal, signal, WritableSignal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { ActivatedRoute, Router } from '@angular/router';
import { CepStateService, QueryParameterFormatterService } from '@libs/shared/common';
import { SearchInputModel } from '@libs/shop/models';
import { NavItemModel} from '@libs/shared/models';
import { PageViewService } from '@libs/shared/services';
import { DeferredPageInfoTracking } from '@libs/shared/tracking';
import { CatalogSearchComponent } from '../catalog-search.component';

@Component({
  selector: 'ui-catalog-search-route',
  templateUrl: './catalog-search-route.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    CatalogSearchComponent,
  ],
  providers: [
    DeferredPageInfoTracking,
  ],
})
export class CatalogSearchRouteComponent implements OnChanges {
  // these inputs get filled from the angular router see bindToComponentInputs
  public q: InputSignal<string | undefined> = input();
  public category: InputSignal<string | undefined> = input();
  public ipp: InputSignal<string | undefined> = input();
  public page: InputSignal<string | undefined> = input();
  public label: InputSignal<string | undefined> = input();
  public sort: InputSignal<string | undefined> = input();
  public manufacturer: InputSignal<string | undefined> = input();
  public own_brand: InputSignal<string | undefined> = input();
  public isCategoryRoute: InputSignal<boolean> = input(false);

  public searchParams: WritableSignal<SearchInputModel | undefined> = signal(undefined);

  public categoryMap: Signal<Map<string, NavItemModel>> = toSignal(this.pageViewService.getCategoryMap(), { initialValue: new Map() });

  public constructor(
    private readonly cepStateService: CepStateService,
    private readonly router: Router,
    private readonly activatedRoute: ActivatedRoute,
    private readonly queryParameterFormatter: QueryParameterFormatterService,
    private readonly pageViewService: PageViewService,
  ) {
  }

  public ngOnChanges(): void {
    const params: SearchInputModel = this.getNormalizedParams();

    if (JSON.stringify(params) === JSON.stringify(this.searchParams())) {
      return;
    }

    if (this.shouldNormalizePath()) {
      void this.navigateToParams(params);

      return;
    }

    this.updateState(params);
  }

  public navigateToParams(params: SearchInputModel): void {
    const replaceUrl: boolean = !(params.q ?? params.category);

    const categoryUrl: string | undefined = params.category ? this.categoryMap().get(params.category)?.url : undefined;
    const changeCategoryUrl: boolean = params.category !== this.category() && !!params.category && !!categoryUrl;

    this.updateState(params);

    if (changeCategoryUrl && this.isCategoryRoute()) {
      void this.router.navigate(
        [categoryUrl],
        {
          queryParams: this.queryParameterFormatter.formatQueryParams({ ...params }),
          replaceUrl: true,
          queryParamsHandling: 'replace',
        },
      );
    } else {
      void this.router.navigate(
        [],
        {
          relativeTo: this.activatedRoute,
          queryParams: this.queryParameterFormatter.formatQueryParams({ ...params }),
          replaceUrl: replaceUrl,
        },
      );
    }

  }

  private updateState(params: SearchInputModel): void {
    this.searchParams.set(params);
    this.cepStateService.setSearchParams(params);
  }

  private shouldNormalizePath(): boolean {
    return this.q() === undefined
      || this.ipp() === undefined
      || this.page() === undefined;
  }

  private getNormalizedParams(): SearchInputModel {
    const stateParams: SearchInputModel = this.cepStateService.searchParams();
    const normalizedParams: SearchInputModel = {
      ...stateParams,
      q: this.q() ?? '',
      page: coerceNumberProperty(this.page() ?? stateParams.page),
      ipp: coerceNumberProperty(this.ipp() ?? stateParams.ipp),
    };

    if (this.label()) {
      normalizedParams.label = this.label();
    } else {
      delete normalizedParams.label;
    }

    if (this.sort()) {
      normalizedParams.sort = this.sort();
    } else {
      delete normalizedParams.sort;
    }

    if (this.manufacturer()) {
      normalizedParams.manufacturer = this.manufacturer();
    } else {
      delete normalizedParams.manufacturer;
    }

    if (this.own_brand()) {
      normalizedParams.own_brand = this.own_brand();
    } else {
      delete normalizedParams.own_brand;
    }

    if (this.category()) {
      normalizedParams.category = this.category();
    } else {
      delete normalizedParams.category;
    }

    return normalizedParams;
  }
}
